'use client';

import { useContext, useState, type FC, useCallback, useMemo } from 'react';
import {
  removeSignature,
  removeSignatureRequest,
  saveSection,
} from '@/_api/client-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import styles from './EngagementLetter.module.css';
import { UserContext } from '@/_contexts/UserProvider';
import {
  Button,
  InlineUnsafe,
  Flex,
  Text,
  TrixEditor,
  Signature,
  ButtonGroup,
  Separator,
  Loading,
  Dialog,
  Alert,
} from '@/_components';
import type { EngagementLetterProps } from './EngagementLetter.types';
import { PREDEFINED_MARKED_NOT_NEEDED } from '@/_constants/questions';
import { EngagementSignature } from '@/_types/models/questionnaire';

export const EngagementLetter: FC<EngagementLetterProps> = ({
  section,
  onRequestSignature,
  questionnaire,
  reloadQuestionnairePreview,
}) => {
  const [loadingSignatureId, setLoadingSignatureId] = useState<number | null>(
    null,
  );
  const [isEditingConfirmationOpen, setIsEditingConfirmationOpen] =
    useState(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoadingSignatureSave, setLoadingSignatureSave] =
    useState<boolean>(false);
  const [editedCustomElement, setEditedCustomElement] = useState<string>(
    section.custom_element || '',
  );
  const [isRemovingSignature, setIsRemovingSignature] = useState(false);
  const [signatureToRemove, setSignatureToRemove] = useState<{
    id: number;
    type: 'signature' | 'request';
  } | null>(null);

  // Get context values once
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  // Memoize derived values to prevent recalculation on every render
  const sectionCompleted = useMemo(
    () => section.marked_completed,
    [section.marked_completed],
  );
  const displayedElement = useMemo(
    () => section.custom_element || section.customElement,
    [section.custom_element, section.customElement],
  );

  const onRemoveSignature = useCallback(
    async (signatureId: number) => {
      try {
        setLoadingSignatureId(signatureId);
        await removeSignature({
          companyId: questionnaire?.company_id!,
          questionnaireId: questionnaire?.id!,
          signatureId,
          token,
        });

        await reloadQuestionnairePreview();
        addToast('success', 'Signature unsigned');
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoadingSignatureId(null);
        setIsRemovingSignature(false);
        setSignatureToRemove(null);
      }
    },
    [questionnaire, token, addToast, reloadQuestionnairePreview],
  );

  const onRemoveSignatureRequest = useCallback(
    async (signatureId: number) => {
      try {
        setLoadingSignatureId(signatureId);
        const response = await removeSignatureRequest({
          companyId: questionnaire?.company_id!,
          questionnaireId: questionnaire?.id!,
          signatureId,
          token,
        });

        if (response.error) {
          addToast('error', response.error);
          return;
        }

        await reloadQuestionnairePreview();
        addToast('success', 'Signature request removed');
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoadingSignatureId(null);
      }
    },
    [questionnaire, token, addToast, reloadQuestionnairePreview],
  );

  const onUpdateEngagementSignature = useCallback(async () => {
    setLoadingSignatureSave(true);

    try {
      await saveSection({
        companyId: questionnaire?.company_id!,
        id: section.id,
        markedCompleted: false,
        setId: questionnaire?.id!,
        customElement: editedCustomElement,
        token,
      });

      await reloadQuestionnairePreview();

      addToast('success', 'Engagement letter updated');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsEditing(false);
      setLoadingSignatureSave(false);
      setIsEditingConfirmationOpen(false);
    }
  }, [
    questionnaire,
    section.id,
    editedCustomElement,
    token,
    addToast,
    reloadQuestionnairePreview,
  ]);

  const onCancelEdit = useCallback(() => {
    setIsEditing(false);
    setIsEditingConfirmationOpen(false);
  }, []);

  const handleRemove = useCallback(
    (signature: EngagementSignature, type: 'signature' | 'request') => {
      if (
        signature.signature === PREDEFINED_MARKED_NOT_NEEDED ||
        !signature.signed_at
      ) {
        if (type === 'signature') {
          onRemoveSignature(signature.id);
        } else if (type === 'request') {
          onRemoveSignatureRequest(signature.id);
        }
      } else {
        setSignatureToRemove({ id: signature.id, type });
        setIsRemovingSignature(true);
      }
    },
    [onRemoveSignature, onRemoveSignatureRequest],
  );

  const handleConfirmRemoval = useCallback(() => {
    if (signatureToRemove !== null) {
      if (signatureToRemove.type === 'signature') {
        onRemoveSignature(signatureToRemove.id);
      } else if (signatureToRemove.type === 'request') {
        onRemoveSignatureRequest(signatureToRemove.id);
      }
    }
    setSignatureToRemove(null);
    setIsRemovingSignature(false);
  }, [signatureToRemove, onRemoveSignature, onRemoveSignatureRequest]);

  const closeRemovalDialog = useCallback(() => {
    setIsRemovingSignature(false);
  }, []);

  const openNewEdit = useCallback(() => {
    setIsEditingConfirmationOpen(true);
  }, []);

  const cancelEditConfirmation = useCallback(() => {
    setIsEditingConfirmationOpen(false);
    setIsEditing(false);
  }, []);

  const confirmEdit = useCallback(() => {
    setIsEditingConfirmationOpen(false);
    setIsEditing(true);
  }, []);

  const requestSignature = useCallback(() => {
    onRequestSignature(section);
  }, [onRequestSignature, section]);

  return (
    <>
      <Dialog
        isOpen={isRemovingSignature}
        onClose={closeRemovalDialog}
        title="Remove Signature Confirmation"
      >
        <Dialog.Content>
          <Flex isColumn gap={8}>
            <Alert type="danger">
              <Text color="critical">
                The client has already signed this engagement letter. Are you
                sure you want to continue?
              </Text>
            </Alert>
            <ButtonGroup align="right">
              <Button
                intent="primary"
                value="Remove"
                onClick={handleConfirmRemoval}
                isLoading={loadingSignatureId === signatureToRemove}
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>
      <Dialog
        isOpen={isEditingConfirmationOpen}
        onClose={cancelEditConfirmation}
        title="Edit Engagement Letter"
      >
        <Dialog.Content>
          <Flex isColumn gap={6}>
            <Text type="small">
              Are you sure you want to edit the engagement letter? The client
              will receive a notification that the engagement letter has been
              edited (if they made progress on the questionnaire)
            </Text>
            <ButtonGroup align="right">
              <Button
                intent="secondary"
                size="small"
                onClick={cancelEditConfirmation}
                value="No"
              />
              <Button
                intent="primary"
                size="small"
                onClick={confirmEdit}
                value="Yes"
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>
      <Flex isColumn={true} gap={6}>
        {isEditing ? (
          <Flex isColumn={true} gap={6}>
            <Flex className={styles.engagementLetter__editor}>
              <TrixEditor
                setValue={setEditedCustomElement}
                isDisabled={isLoadingSignatureSave}
                value={editedCustomElement}
              />
            </Flex>
            <ButtonGroup align="right">
              <Button
                onClick={onCancelEdit}
                value="Cancel"
                intent="secondary"
                size="small"
              />
              <Button
                onClick={onUpdateEngagementSignature}
                value="Save"
                size="small"
                iconRight="check"
                intent="primary"
              />
            </ButtonGroup>
          </Flex>
        ) : (
          <Text type="div">
            <InlineUnsafe text={displayedElement} isExtremelyUnsafe={true} />
          </Text>
        )}
        <Separator />
        {!isEditing && (
          <>
            {section.engagement_signatures?.map((signature) => {
              const isMarkedNotNeeded: boolean =
                signature.signature === PREDEFINED_MARKED_NOT_NEEDED;
              const isLoading = loadingSignatureId === signature.id;
              const isOtherSignatureLoading =
                loadingSignatureId !== null && !isLoading;

              return (
                <Flex key={signature.id} gap={4} isBetween paddingX={2}>
                  {isLoading ? (
                    <Loading size={16} />
                  ) : (
                    <Flex isColumn gap={2}>
                      {signature.signature ? (
                        isMarkedNotNeeded ? (
                          <Text type="small" color="critical">
                            Marked Not Needed
                          </Text>
                        ) : (
                          <Signature name={signature.signature} />
                        )
                      ) : (
                        <Text type="small" color="critical">
                          Unsigned
                        </Text>
                      )}
                      <Text type="tiny" color="secondary">
                        {signature.user_name}
                      </Text>
                    </Flex>
                  )}
                  <ButtonGroup>
                    {!isLoading && (
                      <>
                        {signature.signed_at &&
                          (isMarkedNotNeeded ? (
                            <Button
                              onClick={() =>
                                handleRemove(signature, 'signature')
                              }
                              intent="ghost"
                              size="tiny"
                              value="Undo"
                              iconLeft="arrow-counter-clockwise"
                              isIconOnly
                              withTooltip
                              isLoading={isLoading}
                              isDisabled={isOtherSignatureLoading}
                            />
                          ) : (
                            <Button
                              onClick={() => {
                                handleRemove(signature, 'signature');
                              }}
                              intent="danger-ghost"
                              size="tiny"
                              value="Remove Signature"
                              iconLeft="eraser"
                              isIconOnly
                              withTooltip
                            />
                          ))}
                        <Button
                          onClick={() => handleRemove(signature, 'request')}
                          intent="danger-ghost"
                          size="tiny"
                          iconLeft="file-x"
                          isIconOnly
                          value="Cancel Request"
                          tooltip="Cancel Request"
                          withTooltip
                          isLoading={isLoading}
                          isDisabled={isOtherSignatureLoading}
                        />
                      </>
                    )}
                  </ButtonGroup>
                </Flex>
              );
            })}
            <ButtonGroup align="right">
              <Button
                onClick={requestSignature}
                intent="secondary"
                size="small"
                value="Request Signature"
                iconLeft="plus"
              />
              <Button
                onClick={openNewEdit}
                intent="secondary"
                size="small"
                value="Edit Letter"
                isDisabled={sectionCompleted}
                tooltip="Completed Engagement cannot be edited"
                withTooltip={sectionCompleted}
                iconLeft="pencil"
              />
            </ButtonGroup>
          </>
        )}
      </Flex>
    </>
  );
};
